/**
 * ManageCMDBDiscoveredApps Component
 *
 * This is the main page component for managing CMDB (Configuration Management Database)
 * discovered applications. It provides a tabbed interface for configuring different
 * aspects of discovered integrations like ServiceNow.
 *
 * Key Features:
 * - Tabbed interface for different configuration sections
 * - Loading states and error handling
 * - Dirty state tracking to prevent data loss
 * - Confirmation dialogs for unsaved changes
 *
 * @author Saviynt Development Team
 * @since 2024
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  AlertBanner,
  AlertBannerProvider,
  Box,
  Button,
  Loader,
  ModalIsland,
  TabContent,
  TabGroup,
  TabItem,
} from '@saviynt/design-system';

// Component imports for different features
import IntegrationPageError from '../../components/IntegrationPageError/IntegrationPageError';
import testIds from '../../constants/testIds';
// Custom hooks
import useLocalization from '../../hooks/use-localize';
import useQueryParams from '../../hooks/use-queryParams';
import Logger from '../../services/loggerService';
import AttributeMappings from '../ManageCMDBDiscoveredAppsAttibute/component/AttributeMappings/AttributeMappings';
import ConnectionSettings from '../ManageCMDBDiscoveredAppsConnection/ConnectionSettings';
import ManageCMDBDiscoveredAppsHeader from '../ManageCMDBDiscoveredAppsHeader/ManageCMDBDiscoveredAppsHeader';
import Scheduling from '../ManageCMDBDiscoveredAppsSchedule/component/Scheduling/Scheduling';

// Constants and types
import DISCOVERY_CONFIG_TAB_LABEL_FALLBACK_MAPPINGS from './constants/ManageCMDBDiscoveredAppsConstant';
import type { Tab, TabDetails } from './data-layer/discoveryConfigByIdTypes';
import { useGetDiscoveryConfigByIdQuery } from './data-store/getDiscoveryConfigByIdSlice';

// Styles
import './manageCMDBDiscoveredApps.css';

/**
 * Interface defining the structure of a tab in the configuration interface
 *
 * @interface TabDefinition
 * @property {number} instanceKey - Unique identifier for the discovery config instance
 * @property {string} instanceName - Human-readable name of the instance
 * @property {string} name - Internal name/key of the tab (e.g., 'Connection_Settings')
 * @property {string} labelKey - Localization key for the tab label
 * @property {TabDetails} details - Configuration data specific to this tab
 * @property {number} sortOrder - Order in which tabs should be displayed
 * @property {React.ComponentType<any>} Component - React component to render for this tab
 */
interface TabDefinition {
  instanceKey: number;
  instanceName: string;
  name: string;
  labelKey: string;
  details: TabDetails;
  sortOrder: number;
  Component: React.ComponentType<any>;
}

/**
 * Maps tab names to their corresponding React components
 * This allows dynamic rendering of different tab content based on the tab name
 *
 * @constant {Record<string, React.ComponentType<any>>}
 */
const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
  Connection_Settings: ConnectionSettings, // Component for database connection configuration
  Scheduling, // Component for scheduling discovery jobs
  Attribute_Mapping: AttributeMappings, // Component for mapping attributes between systems
};

/**
 * Main component for managing CMDB discovered applications
 *
 * This component handles:
 * 1. Fetching discovery configuration data from the API
 * 2. Rendering a tabbed interface for different configuration sections
 * 3. Managing loading and error states
 * 4. Tracking dirty state to prevent accidental data loss
 * 5. Showing confirmation dialogs when switching tabs with unsaved changes
 *
 * @returns {React.ReactElement | null} The rendered component or null if no data
 *
 * @example
 * // This component is typically used in a route:
 * <Route path="/integrations/discovery/:id" component={ManageCMDBDiscoveredApps} />
 */
function ManageCMDBDiscoveredApps(): React.ReactElement | null {
  // Router and navigation hooks
  const history = useHistory(); // For programmatic navigation
  const { id } = useQueryParams(); // Extract 'id' from URL query parameters
  const configId = Number(id); // Convert string ID to number for API calls
  const formatMessage = useLocalization(); // Hook for translating text

  /**
   * Fetch discovery configuration data using RTK Query
   * This hook automatically handles caching, loading states, and refetching
   */
  const {
    data: discoveryConfig, // The fetched configuration data
    isLoading, // True during initial load
    isFetching, // True during any fetch (including refetch)
    isError, // True if the request failed
    refetch, // Function to manually refetch data
  } = useGetDiscoveryConfigByIdQuery(configId);

  /**
   * Loading state management
   * We only show the full-page loader on the very first load, not on subsequent refetches
   * This provides better UX by not blocking the entire interface during background updates
   */
  const initialLoadRef = useRef(true); // Track if this is the first load
  const [initialLoadCount, setInitialLoadCount] = useState(0); // Count completed loads

  // Update loading tracking when load state changes
  useEffect(() => {
    if (initialLoadRef.current && !isLoading) {
      initialLoadRef.current = false;
      setInitialLoadCount((c) => c + 1);
    }
  }, [isLoading]);

  // Show full-page loader only during initial load
  const showFullPageLoader = isLoading && initialLoadCount === 0;

  /**
   * Reset version management
   * This version number is passed to child components to trigger resets
   * when data is refetched. It helps components know when to clear their local state.
   */
  const [resetVersion, setResetVersion] = useState(0);
  const prevFetchingRef = useRef(false); // Track previous fetching state

  // Increment reset version after each successful refetch (but not initial load)
  useEffect(() => {
    // If we were fetching and now we're not, and it's not the initial load
    if (prevFetchingRef.current && !isFetching && initialLoadCount > 0) {
      setResetVersion((v) => v + 1); // Increment to trigger child component resets
    }

    prevFetchingRef.current = isFetching; // Update previous state
  }, [isFetching, initialLoadCount]);

  /**
   * Process and sort tabs for display
   *
   * This memoized computation:
   * 1. Takes raw tab data from the API
   * 2. Sorts tabs by their sortOrder property
   * 3. Filters out tabs that don't have corresponding components
   * 4. Creates TabDefinition objects with all necessary props
   *
   * @returns {TabDefinition[]} Array of processed tab definitions
   */
  const sortedTabs = useMemo<TabDefinition[]>(() => {
    // Return empty array if no config data yet
    if (!discoveryConfig?.tabs) {
      return [];
    }

    return discoveryConfig.tabs
      .slice() // Create a copy to avoid mutating original array
      .sort((a: Tab, b: Tab) => a.sortOrder - b.sortOrder) // Sort by display order
      .reduce<TabDefinition[]>((acc: TabDefinition[], tab: Tab) => {
        // Look up the component for this tab type
        const Component = COMPONENT_MAP[tab.name];

        // Only include tabs that have a corresponding component
        if (Component) {
          acc.push({
            instanceKey: discoveryConfig.id, // Pass instance ID to child
            instanceName: discoveryConfig.name, // Pass instance name to child
            name: tab.name, // Internal tab identifier
            labelKey: tab.label, // Localization key for tab title
            details: tab.details, // Tab-specific configuration data
            sortOrder: tab.sortOrder, // Display order
            Component, // React component to render
          });
        }

        return acc;
      }, []);
  }, [discoveryConfig]); // Recalculate when config data changes

  /**
   * Active tab state management
   *
   * We maintain the active tab in both state and a ref:
   * - State: For React rendering and UI updates
   * - Ref: For accessing current value in callbacks without stale closures
   */
  const [activeTab, setActiveTab] = useState<string>('');

  useEffect(() => {
    if (sortedTabs.length > 0 && activeTab === '') {
      setActiveTab(sortedTabs[0].name);
    }
  }, [sortedTabs, activeTab]);
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // — NEW: memoized dirty-handlers map —
  const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({});
  const dirtyHandlers = useMemo(
    () =>
      sortedTabs.reduce<Record<string, (isDirty: boolean) => void>>(
        (
          acc: Record<string, (isDirty: boolean) => void>,
          { name }: TabDefinition
        ) => {
          acc[name] = (isDirty: boolean) => {
            setDirtyTabs((prev) => ({ ...prev, [name]: isDirty }));
          };

          return acc;
        },
        {}
      ),
    [sortedTabs]
  );

  // — tab switching with confirmation (unchanged) —
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const handleTabChange = useCallback(
    (next: string) => {
      if (next === activeTabRef.current) {
        return;
      }

      if (!activeTabRef.current) {
        setActiveTab(next);

        return;
      }

      if (dirtyTabs[activeTabRef.current]) {
        setPendingTab(next);
        setIsConfirmOpen(true);
      } else {
        setActiveTab(next);
      }
    },
    [dirtyTabs]
  );
  const handleDiscard = useCallback(() => {
    setDirtyTabs((prev) => ({
      ...prev,
      [activeTabRef.current]: false,
    }));

    if (pendingTab) {
      setActiveTab(pendingTab);
    }

    setPendingTab(null);
    setIsConfirmOpen(false);
  }, [pendingTab]);
  const handleCancel = useCallback(() => {
    setPendingTab(null);
    setIsConfirmOpen(false);
  }, []);

  // — banner (unchanged) —
  const bannerConfig = {
    kind: '' as const,
    colorTheme: '',
    title: '',
    description: '',
  };
  const isBannerVisible = false;

  // — loading & error guards (unchanged) —
  if (showFullPageLoader) {
    return (
      <Box data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_CONTAINER}>
        <section
          className='Discovery-config-loader'
          data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_LOADER}>
          <Loader color='brand' format='main' kind='loop' />
        </section>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_CONTAINER}>
        <IntegrationPageError
          showBack
          handleBackButtonCallback={() => history.push('/integrations')}
          illustrationKind='Error'
          heading='INTEGRATIONS.DISCOVERY.ERROR.HEADING'
          subHeading='INTEGRATIONS.DISCOVERY.ERROR.SUBHEADING'
          reload={async () => {
            await refetch();
          }}
        />
      </Box>
    );
  }

  if (!discoveryConfig) {
    return null;
  }

  const headerDetails = {
    name: discoveryConfig.name,
    discoverySource: discoveryConfig.discoverySource,
    imageName: discoveryConfig.logoName,
    status: discoveryConfig.status,
    id: discoveryConfig.id,
    appTypeKey: discoveryConfig.appTypeKey,
  };

  return (
    <Box data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_CONTAINER}>
      <div className='Discovery-config'>
        <AlertBannerProvider>
          <AlertBanner
            kind={bannerConfig.kind}
            colorTheme={bannerConfig.colorTheme}
            title={bannerConfig.title}
            description={bannerConfig.description}
            isVisible={isBannerVisible}
            onCancel={() => {}}
            dataTestId={testIds.MANAGE_CMDB_DISCOVERED_APPS_ALERT_BANNER}
          />
        </AlertBannerProvider>

        <header
          className='Discovery-config-pageHeader'
          role='banner'
          data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_PAGE_HEADER}>
          <ManageCMDBDiscoveredAppsHeader headerDetails={headerDetails} />

          {activeTab && (
            <nav
              aria-label={formatMessage(
                'INTEGRATIONS.DISCOVERY.TABS_ARIA_LABEL'
              )}>
              <TabGroup
                className='Discovery-config-tabs'
                value={activeTab}
                onChange={handleTabChange}
                dataTestId={testIds.MANAGE_CMDB_DISCOVERED_APPS_TAB_GROUP}>
                {sortedTabs.map(({ name, labelKey }) => (
                  <TabItem
                    key={name}
                    value={name}
                    label={formatMessage(
                      labelKey,
                      DISCOVERY_CONFIG_TAB_LABEL_FALLBACK_MAPPINGS[labelKey]
                    )}
                  />
                ))}
              </TabGroup>
            </nav>
          )}
        </header>

        <main
          className='Discovery-config-content'
          data-testid={testIds.MANAGE_CMDB_DISCOVERED_APPS_MAIN_CONTENT}>
          {sortedTabs.map(
            ({
              name,
              Component,
              details,
              instanceKey,
              instanceName,
              sortOrder,
            }) =>
              name === activeTab ? (
                <TabContent
                  key={name}
                  aria-labelledby={`tab-${name}`}
                  value={activeTab}
                  tabValue={name}>
                  <Component
                    instanceKey={instanceKey}
                    instanceName={instanceName}
                    details={details}
                    status={discoveryConfig.status}
                    sortOrder={sortOrder}
                    resetVersion={resetVersion}
                    onDirtyChange={dirtyHandlers[name]}
                    onSaveError={(msg: string) => Logger.error('error', msg)}
                    onSaveSuccess={(msg: string) => Logger.info('Success', msg)}
                  />
                </TabContent>
              ) : null
          )}
        </main>

        <ModalIsland
          kind='warning'
          alertTitle={formatMessage(
            'INTEGRATIONS.DISCOVERY.DISCARD_UNSAVED_CHANGES_TITLE'
          )}
          alertSubtitle={formatMessage(
            'INTEGRATIONS.DISCOVERY.DISCARD_UNSAVED_CHANGES_SUBTITLE'
          )}
          isOpen={isConfirmOpen}
          onClose={handleCancel}
          dataTestId={testIds.MANAGE_CMDB_DISCOVERED_APPS_DISCARD_MODAL}
          primaryButton={
            <Button kind='outlined' size='large' onClick={handleDiscard}>
              {formatMessage('INTEGRATIONS.BUTTON.DISCARD_CHANGES')}
            </Button>
          }
          secondaryButton={
            <Button kind='ghost' size='medium' onClick={handleCancel}>
              {formatMessage('INTEGRATIONS.BUTTON.CANCEL')}
            </Button>
          }
        />
      </div>
    </Box>
  );
}

export default React.memo(ManageCMDBDiscoveredApps);
